import { GateRuntimeBootstrapPayload } from "modules/gate/types/gate";
import { normalizePlatformTheme, platformThemeCssVariables } from "app/theme/platformTheme";
import {
    GATE_FORM_RUNTIME_SCRIPT_SRC,
    GATE_FORM_RUNTIME_STYLE_HREF,
} from "modules/gate/form-runtime/assets";
import { resolveGateRuntimeFeatures } from "modules/gate/services/gateRuntimeFeatures";

const escapeForInlineScript = (value: string) =>
    value
        .replace(/\\/g, "\\\\")
        .replace(/`/g, "\\`")
        .replace(/\$\{/g, "\\${")
        .replace(/<\/script>/gi, "<\\/script>");

const escapeForInlineStyle = (value: string) =>
    value.replace(/<\/style>/gi, "<\\/style>");

// Unlike escapeForInlineScript, this is for raw JS source embedded directly as <script>
// content (not re-parsed through a JS template literal first), so it must NOT double
// backslashes — doubling here would corrupt every escape sequence/regex in the source
// (e.g. Vue's own minified code), since the browser parses this text as JS exactly once.
const escapeForRawScriptContent = (value: string) =>
    value.replace(/<\/script>/gi, "<\\/script>");

export const buildGateRuntimeHtml = (payload: GateRuntimeBootstrapPayload) => {
    const features = payload.features || resolveGateRuntimeFeatures(payload.page);
    const normalizedHead = features.tailwindJit
        ? (payload.page.head || "").replace(/<script[^>]+src=["']https:\/\/cdn\.tailwindcss\.com[^"']*["'][^>]*><\/script>/gi, "")
        : payload.page.head || "";
    const serializedPayload = escapeForInlineScript(JSON.stringify(payload));
    const theme = normalizePlatformTheme(payload.theme);
    const locale = String(payload.i18n?.locale || "en-US").replace(/[^A-Za-z0-9-]/g, "") || "en-US";
    const themeVariables = Object.entries(platformThemeCssVariables(theme))
        .map(([name, value]) => `${name}: ${escapeForInlineStyle(String(value))};`)
        .join("\n        ");
    const pageStyle = payload.page.style ? escapeForInlineStyle(payload.page.style) : "";
    const widgetStyles = (payload.page.widgets || [])
        .map((widget) => widget?.style?.trim())
        .filter((style): style is string => Boolean(style))
        .map((style) => escapeForInlineStyle(style))
        .join("\n");
    const formRuntimeAssets = features.formRuntime ? {
        scriptSrc: GATE_FORM_RUNTIME_SCRIPT_SRC,
        styleHref: GATE_FORM_RUNTIME_STYLE_HREF,
    } : null;
    const formRuntimeLoader = formRuntimeAssets ? `
    <script>
      (async function () {
        const formRuntimeAssets = ${JSON.stringify({
            scriptSrc: GATE_FORM_RUNTIME_SCRIPT_SRC,
            styleHref: GATE_FORM_RUNTIME_STYLE_HREF,
        })};
        let formRuntimePromise = null;

        window.__prmEnsureFormRuntime = function () {
          if (window.__prmRegisterFormComponents && window.__prmComponents) {
            return Promise.resolve(window.__prmComponents);
          }

          if (formRuntimePromise) {
            return formRuntimePromise;
          }

          formRuntimePromise = new Promise(function (resolve, reject) {
            if (formRuntimeAssets.styleHref && !document.querySelector("link[data-prm-form-runtime=style]")) {
              const link = document.createElement("link");
              link.rel = "stylesheet";
              link.href = formRuntimeAssets.styleHref;
              link.setAttribute("data-prm-form-runtime", "style");
              document.head.appendChild(link);
            }

            const existingScript = document.querySelector("script[data-prm-form-runtime=script]");
            if (existingScript) {
              existingScript.addEventListener("load", function () { resolve(window.__prmComponents || {}); }, { once: true });
              existingScript.addEventListener("error", function () {
                const error = new Error("Gate Form Runtime failed to load: " + existingScript.src);
                reportRuntimeError("form-runtime-load", error, existingScript.src);
                reject(error);
              }, { once: true });
              return;
            }

            const script = document.createElement("script");
            script.src = formRuntimeAssets.scriptSrc;
            script.async = true;
            script.setAttribute("data-prm-form-runtime", "script");
            script.onload = function () { resolve(window.__prmComponents || {}); };
            script.onerror = function () {
              const error = new Error("Gate Form Runtime failed to load: " + script.src);
              reportRuntimeError("form-runtime-load", error, script.src);
              reject(error);
            };
            document.head.appendChild(script);
          });

          return formRuntimePromise;
        };
      })();
    </script>` : "";
    const tailwindRuntime = features.tailwindJit ? `
    <script src="/vendor/tailwindcss-3.4.17.js"></script>
    <script>
      // The vendored Tailwind Play/CDN build replaces window.tailwind with its own
      // reactive Proxy (config starting empty) the instant this script tag runs —
      // see the bottom of tailwindcss-3.4.17.js: "window.tailwind = new Proxy({config:
      // {}, ...}, ...)". That unconditionally overwrites anything assigned to
      // window.tailwind *before* this script loads, so setting the config in a
      // preceding <script> (the pattern Tailwind's own docs show) is silently
      // discarded here. The Proxy's "set" trap is what triggers a CSS rebuild, so
      // the config must be assigned as a property (window.tailwind.config = ...)
      // *after* this script has already run and installed the Proxy. Confirmed
      // empirically against this exact vendored file before writing this fix —
      // every prm-* custom token (colors.prm, rounded-prm, shadow-prm) generates no
      // CSS at all otherwise, silently falling back to Tailwind's default palette.
      window.tailwind.config = {
        theme: {
          extend: {
            colors: {
              prm: {
                bg: "var(--prm-bg)",
                surface: "var(--prm-surface)",
                border: "var(--prm-border)",
                text: "var(--prm-text)",
                primary: "var(--prm-primary)",
                error: "var(--prm-error)"
              }
            },
            borderRadius: { prm: "var(--prm-radius, 6px)" },
            boxShadow: { prm: "var(--prm-shadow, 0 12px 30px rgba(15, 23, 42, 0.12))" }
          }
        }
      };
    </script>` : "";

    return `<!DOCTYPE html>
<html lang="${locale}">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>${payload.page.title || payload.page.name}</title>
    <style>
      html, body, #gate-app {
        width: 100%;
        height: 100%;
        margin: 0;
      }

      :root {
        color-scheme: ${theme.mode === "dark" ? "dark" : "light"};
        ${themeVariables}
      }

      body {
        font-family: Arial, sans-serif;
        background: var(--prm-bg);
        color: var(--prm-text);
      }

      *:focus-visible {
        outline: 3px solid var(--prm-text-primary);
        outline-offset: 2px;
      }

      [v-cloak] {
        display: none;
      }

    </style>
    ${normalizedHead}
    ${pageStyle ? `<style>${pageStyle}</style>` : ""}
    ${widgetStyles ? `<style>${widgetStyles}</style>` : ""}
  </head>
  <body>
    <div id="gate-app" v-cloak></div>
    <script>
      const source = "gate-runtime";
      const reportedRuntimeErrors = new WeakSet();
      function reportRuntimeError(phase, error, url) {
        if (error && typeof error === "object") {
          if (reportedRuntimeErrors.has(error)) return;
          reportedRuntimeErrors.add(error);
        }
        const message = error && error.message ? error.message : String(error);
        const stack = error && error.stack ? String(error.stack) : null;
        window.parent.postMessage({ source, type: "gate:error", payload: { phase, message, stack, url: url || null } }, "*");
      }
      window.addEventListener("error", function (event) {
        reportRuntimeError("window-error", event.error || new Error(event.message || "Gate runtime error"), event.filename);
      });
      window.addEventListener("unhandledrejection", function (event) {
        reportRuntimeError("window-error", event.reason || new Error("Unhandled Gate rejection"));
      });
      window.__prmGateRuntimeAssetError = function (asset) {
        const root = document.getElementById("gate-app");
        if (!root) return;
        root.removeAttribute("v-cloak");
        root.innerHTML = '<pre role="alert" style="padding:16px;white-space:pre-wrap;color:#b91c1c;">Gate runtime failed to load ' + asset + '.</pre>';
      };
      window.__prmGateTiming = { vueScriptStart: performance.now() };
    </script>
    ${tailwindRuntime}
    <script>${escapeForRawScriptContent(payload.vueRuntimeSource || "")}</script>
    <script>
      window.__prmGateTiming.vueScriptEnd = performance.now();
      if (!window.Vue) window.__prmGateRuntimeAssetError("Vue");
    </script>
    ${formRuntimeLoader}
    <script>
      (async function () {
        const payload = JSON.parse(\`${serializedPayload}\`);
        const pendingRequests = new Map();
        let runtimeSession = payload.session || null;
        const gateTheme = window.Vue.reactive(payload.theme || {});
        const gateCurrency = window.Vue.reactive(payload.currency || {
          currencies: [],
          defaultCurrencyCode: null,
          defaultNumberFormat: null,
          userNumberFormat: null
        });
        const routeState = window.Vue.reactive(payload.location || {
          origin: "",
          href: "",
          pathname: "",
          routePath: "/",
          search: "",
          hash: "",
          params: {},
          query: {}
        });
        if (!routeState.routePath) routeState.routePath = "/";
        const toSerializable = function (value, seen) {
          if (value === undefined || value === null) return value;
          if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return value;
          if (typeof ArrayBuffer !== "undefined" && value instanceof ArrayBuffer) return value;
          if (typeof ArrayBuffer !== "undefined" && ArrayBuffer.isView && ArrayBuffer.isView(value)) return value;
          if (typeof File !== "undefined" && value instanceof File) return value;
          if (typeof Blob !== "undefined" && value instanceof Blob) return value;
          if (typeof Date !== "undefined" && value instanceof Date) return value.toISOString();
          if (typeof FileList !== "undefined" && value instanceof FileList) {
            return Array.from(value).map(function (item) { return toSerializable(item, seen); });
          }
          if (Array.isArray(value)) {
            return value.map(function (item) { return toSerializable(item, seen); });
          }
          if (typeof value === "object") {
            const visited = seen || new WeakSet();
            if (visited.has(value)) {
              throw new Error("Gate request payload contains a circular reference");
            }
            visited.add(value);

            const output = {};
            Object.keys(value).forEach(function (key) {
              const item = value[key];
              if (typeof item !== "function") {
                output[key] = toSerializable(item, visited);
              }
            });
            visited.delete(value);
            return output;
          }

          return undefined;
        };

        const toGateFilePayload = async function (file) {
          if (!(typeof File !== "undefined" && file instanceof File)) {
            return file;
          }

          return {
            __gateFile: true,
            name: file.name,
            type: file.type,
            lastModified: file.lastModified,
            size: file.size,
            buffer: await file.arrayBuffer()
          };
        };

        const prepareEmailAttachmentPayload = async function (input) {
          const payload = input && typeof input === "object" ? Object.assign({}, input) : {};
          payload.file = await toGateFilePayload(payload.file);
          return payload;
        };

        const prepareAttachmentPayload = async function (input) {
          const payload = input && typeof input === "object" ? Object.assign({}, input) : {};
          if (payload.file) {
            payload.file = await toGateFilePayload(payload.file);
          }
          return payload;
        };
        const translateLookup = function (messages, key) {
          if (!messages || typeof messages !== "object" || !key) return undefined;

          return key.split(".").reduce(function (current, part) {
            if (!current || typeof current !== "object") {
              return undefined;
            }

            return current[part];
          }, messages);
        };

        const normalizeLocale = function (value) {
          const rawValue = Array.isArray(value) ? value[0] : value;
          const normalizedValue = String(rawValue || "").trim();
          const lowerValue = normalizedValue.toLowerCase();

          if (lowerValue.indexOf("pt") === 0) return "pt-BR";
          if (lowerValue.indexOf("en") === 0) return "en-US";

          return normalizedValue || undefined;
        };

        const readLocaleOption = function (localeOrOptions) {
          if (typeof localeOrOptions === "string") {
            return normalizeLocale(localeOrOptions);
          }

          if (localeOrOptions && typeof localeOrOptions === "object") {
            return normalizeLocale(localeOrOptions.locale || localeOrOptions.language);
          }

          return undefined;
        };

        const interpolateTranslation = function (value, params) {
          if (typeof value !== "string" || !params || typeof params !== "object") {
            return value;
          }

          return value.replace(/{{\\s*([^{}\\s]+)\\s*}}/g, function (_, token) {
            const replacement = params[token];
            return replacement === undefined || replacement === null ? "" : String(replacement);
          });
        };

        const gateI18n = window.Vue.reactive({
          locale: payload.i18n?.locale || "en-US",
          fallbackLocale: payload.i18n?.fallbackLocale || "en-US",
          messages: payload.i18n?.messages || {},
          messagesByLocale: payload.i18n?.messagesByLocale || {},
          supportedLocales: Array.isArray(payload.i18n?.supportedLocales) ? payload.i18n.supportedLocales : [],
          isAuthenticated: Boolean(payload.i18n?.isAuthenticated),
          onLocaleChange(handler) {
            if (typeof handler !== "function") return function () {};
            const listener = function (event) { handler(event.detail); };
            window.addEventListener("gate:locale-changed", listener);
            return function () { window.removeEventListener("gate:locale-changed", listener); };
          },
          setLocale(locale) {
            const nextLocale = normalizeLocale(locale);
            if (!nextLocale || this.supportedLocales.indexOf(nextLocale) < 0) {
              return this.locale;
            }

            if (this.isAuthenticated) {
              return this.locale;
            }

            this.locale = nextLocale;
            document.documentElement.lang = nextLocale;
            this.messages = this.messagesByLocale[nextLocale] || {};

            try {
              window.localStorage && window.localStorage.setItem("prm.gate.locale", nextLocale);
            } catch (_error) {}

            try {
              window.parent.postMessage({
                source,
                type: "gate:locale-change",
                payload: { locale: nextLocale }
              }, "*");
            } catch (_postError) {}

            return this.locale;
          },
          getMessages(localeOrOptions) {
            const requestedLocale = readLocaleOption(localeOrOptions);

            if (!requestedLocale || requestedLocale === this.locale) {
              return this.messages;
            }

            return this.messagesByLocale[requestedLocale] || {};
          },
          lookup(key, localeOrOptions) {
            const requestedLocale = readLocaleOption(localeOrOptions);

            if (!requestedLocale || requestedLocale === this.locale) {
              return translateLookup(this.messages, key);
            }

            const translated = translateLookup(this.messagesByLocale[requestedLocale] || {}, key);

            if (translated !== undefined) {
              return translated;
            }

            return translateLookup(this.messagesByLocale[this.fallbackLocale] || {}, key);
          },
          has(key, localeOrOptions) {
            return this.lookup(key, localeOrOptions) !== undefined;
          },
          t(key, params, fallback, localeOrOptions) {
            const translated = this.lookup(key, localeOrOptions);

            if (typeof translated === "string") {
              return interpolateTranslation(translated, params);
            }

            if (translated !== undefined && translated !== null) {
              return translated;
            }

            if (typeof fallback === "string") {
              return interpolateTranslation(fallback, params);
            }

            return key;
          }
        });

        function createGate(widgetCode) {
          const gate = {
            get session() { return runtimeSession; },
            set session(value) { runtimeSession = value; },
            invoke(method, requestPayload) {
              return new Promise((resolve, reject) => {
                const requestId = method + ":" + Date.now() + ":" + Math.random().toString(16).slice(2);
                let serializablePayload;

                try {
                  serializablePayload = toSerializable(requestPayload);
                } catch (error) {
                  reject(new Error("Gate request payload is not serializable"));
                  return;
                }

                pendingRequests.set(requestId, { resolve, reject });

                window.parent.postMessage({
                  source,
                  type: "gate:invoke",
                  requestId,
                  method,
                  payload: serializablePayload,
                  widgetCode
                }, "*");
              });
            },
            api: {
              auth: {
                getSession() {
                  if (gate.session) {
                    return Promise.resolve(gate.session);
                  }
                  return gate.invoke("auth.session");
                },
                login(input) { return gate.invoke("auth.login", input); },
                rememberLogin(input) { return gate.invoke("auth.login.remember", input); },
                getRememberedLogin() { return gate.invoke("auth.login.remembered"); },
                loginRedirect(returnTo) { return gate.invoke("auth.login_redirect", { returnTo }); },
                logout() { return gate.invoke("auth.logout"); }
              },
              record: {
                query(input) { return gate.invoke("record.query", input); },
                get(input) { return gate.invoke("record.get", input); },
                create(input) { return gate.invoke("record.create", input); },
                update(input) { return gate.invoke("record.update", input); },
                delete(input) { return gate.invoke("record.delete", input); }
              },
              email: {
                send(input) { return gate.invoke("email.send", input); },
                sendTemplate(input) { return gate.invoke("email.send_template", input); },
                renderTemplate(input) { return gate.invoke("email.render_template", input); },
                uploadAttachment(input) { return prepareEmailAttachmentPayload(input).then(function (payload) { return gate.invoke("email.upload_attachment", payload); }); },
                deleteAttachment(input) { return gate.invoke("email.delete_attachment", input); }
              },
              attachments: {
                list(input) { return gate.invoke("attachment.list", input); },
                upload(input) { return prepareAttachmentPayload(input).then(function (payload) { return gate.invoke("attachment.upload", payload); }); },
                delete(input) { return gate.invoke("attachment.delete", input); },
                downloadUrl(input) { return gate.invoke("attachment.download_url", input); },
                viewUrl(input) { return gate.invoke("attachment.view_url", input); }
              },
              servicePortal: {
                getForm(input) { return gate.invoke("service_portal.form.get", input); },
                submit(input) { return gate.invoke("service_portal.submit", input); },
                getPublicRequest(input) { return gate.invoke("service_portal.request.public_get", input); },
                lookupReference(input) { return gate.invoke("service_portal.reference.lookup", input); },
                listAttachments(input) { return gate.invoke("service_portal.attachment.list", input); },
                uploadAttachment(input) { return prepareAttachmentPayload(input).then(function (payload) { return gate.invoke("service_portal.attachment.upload", payload); }); },
                deleteAttachment(input) { return gate.invoke("service_portal.attachment.delete", input); },
                downloadAttachmentUrl(input) { return gate.invoke("service_portal.attachment.download_url", input); },
                viewAttachmentUrl(input) { return gate.invoke("service_portal.attachment.view_url", input); }
              },
              session: {
                createUser(input) { return gate.invoke("session.user.create", input); },
                updateUser(input) { return gate.invoke("session.user.update", input); },
                updatePassword(input) { return gate.invoke("session.user.password", input); }
              },
              translations: {
                coverage(input) { return gate.invoke("translations.coverage", input); }
              }
            },
            router: {
              current: routeState,
              get path() { return routeState.pathname; },
              get routePath() { return routeState.routePath || "/"; },
              get params() { return routeState.params || {}; },
              get query() { return routeState.query || {}; },
              navigate(to, options) {
                const normalizedOptions = options && typeof options === "object" ? options : {};
                return gate.invoke("router.navigate", {
                  to,
                  replace: normalizedOptions.replace,
                  state: normalizedOptions.state
                });
              },
              navigateLocal(to, options) {
                const normalizedOptions = options && typeof options === "object" ? options : {};
                return gate.invoke("router.navigate", {
                  to,
                  replace: normalizedOptions.replace,
                  state: normalizedOptions.state,
                  scope: "local"
                });
              }
            },
            ui: {
              notify(message, level) { return gate.invoke("ui.notify", { message, level }); }
            },
            modal: {
              data: payload.modal?.data || {},
              submit(data) {
                return gate.invoke("modal.submit", {
                  action: "confirm",
                  data: data && typeof data === "object" && "data" in data ? data.data : data
                });
              },
              close(result) {
                const payload = result && typeof result === "object" ? result : {};
                return gate.invoke("modal.close", {
                  action: payload.action || "close",
                  data: payload.data
                });
              },
              cancel(data) {
                return gate.invoke("modal.close", {
                  action: "cancel",
                  data
                });
              }
            },
            i18n: gateI18n,
            currency: gateCurrency,
            theme: gateTheme,
            page: payload.page
          };

          return gate;
        }

        const pageGate = createGate(undefined);
        const Gate = pageGate;

        function createScopedGate(widgetCode) {
          if (!widgetCode) {
            return pageGate;
          }

          return createGate(widgetCode);
        }

        window.Gate = Gate;

        function compileDefinition(definition, fallbackTemplate, page, scopedGate) {
          if (!definition || typeof definition !== "string" || !definition.trim()) {
            return withGateTemplateContext({ template: fallbackTemplate }, page, scopedGate || pageGate);
          }

          const compiled = (new Function("Vue", "Gate", "page", "return " + definition))(window.Vue, scopedGate || pageGate, page);
          if (compiled && typeof compiled === "object" && !compiled.template && fallbackTemplate) {
            compiled.template = fallbackTemplate;
          }
          return withGateTemplateContext(compiled, page, scopedGate || pageGate);
        }

        function withGateTemplateContext(component, page, gate) {
          const normalizedComponent = component && typeof component === "object" ? component : {};
          const originalData = normalizedComponent.data;

          return Object.assign({}, normalizedComponent, {
            data() {
              const originalState = typeof originalData === "function"
                ? originalData.call(this)
                : {};
              const state = originalState && typeof originalState === "object" ? originalState : {};

              return Object.assign({}, state, {
                Gate: gate,
                page
              });
            }
          });
        }

        function registerPageComponents(app, page) {
          const components = page.components || {};

          Object.keys(components).forEach(function (name) {
            try {
              const component = compileDefinition(components[name], "", page, pageGate);
              app.component(name, component);
            } catch (error) {
              console.error("Failed to register Gate component", name, error);
            }
          });
        }

        function registerWidgets(app, page) {
          const widgets = Array.isArray(page.widgets) ? page.widgets : [];
          const widgetRegistry = {};

          widgets.forEach(function (widget) {
            if (!widget || !widget.code) return;

            const fallbackTemplate = widget.template || '<section style="padding:12px;border:1px solid #e5e7eb;">Widget "' + widget.code + '" has no template.</section>';
            const widgetGate = createScopedGate(widget.code);

            try {
              const component = compileDefinition(widget.script, fallbackTemplate, page, widgetGate);
              app.component(widget.code, component);
              widgetRegistry[widget.code] = widget;
            } catch (error) {
              console.error("Failed to register Gate widget", widget.code, error);
            }
          });

          Gate.widgets = widgetRegistry;
        }

        window.addEventListener("message", function (event) {
          if (!event.data || event.data.source !== "gate-host") return;

          if (event.data.type === "gate:context-sync" && event.data.payload) {
            const context = event.data.payload;
            if (context.i18n) {
              const previousLocale = gateI18n.locale;
              Object.assign(gateI18n, context.i18n);
              document.documentElement.lang = gateI18n.locale || "en-US";
              if (context.i18n.locale && context.i18n.locale !== previousLocale) {
                window.dispatchEvent(new CustomEvent("gate:locale-changed", {
                  detail: { locale: gateI18n.locale, previousLocale }
                }));
              }
            }
            if (context.theme) Object.assign(gateTheme, context.theme);
            if (context.currency) Object.assign(gateCurrency, context.currency);
            if ("session" in context) runtimeSession = context.session || null;
            if (context.themeCssVariables) {
              Object.keys(context.themeCssVariables).forEach(function (name) {
                document.documentElement.style.setProperty(name, context.themeCssVariables[name]);
              });
            }
            return;
          }

          if (event.data.type === "gate:location-sync" && event.data.payload) {
            const nextLocation = event.data.payload;
            routeState.origin = nextLocation.origin || routeState.origin || "";
            routeState.href = nextLocation.href || routeState.href || "";
            routeState.pathname = nextLocation.pathname || "";
            routeState.routePath = nextLocation.routePath || "/";
            routeState.search = nextLocation.search || "";
            routeState.hash = nextLocation.hash || "";
            routeState.params = nextLocation.params || {};
            routeState.query = nextLocation.query || {};
            return;
          }

          if (!event.data.requestId) return;

          const pending = pendingRequests.get(event.data.requestId);
          if (!pending) return;

          pendingRequests.delete(event.data.requestId);

          if (event.data.type === "gate:resolve") {
            pending.resolve(event.data.payload);
            return;
          }

          const rejection = event.data.error;
          const rejectionMessage = (rejection && typeof rejection === "object" ? rejection.message : rejection) || "Gate request failed";
          const rejectionError = new Error(rejectionMessage);
          if (rejection && typeof rejection === "object") {
            if (rejection.code) rejectionError.code = rejection.code;
            if (rejection.details !== undefined) rejectionError.details = rejection.details;
            if (rejection.status) rejectionError.status = rejection.status;
          }
          pending.reject(rejectionError);
        });

        const root = document.getElementById("gate-app");
        if (!root) return;

        const page = payload.page || {};
        const fallbackTemplate = (page.template && page.template.trim())
          ? page.template
          : '<div style="padding:16px">Gate page loaded but no script was provided.</div>';

        function normalizeRoutePath(path) {
          const normalized = String(path || "/").trim();
          if (!normalized || normalized === "/") return "/";
          return "/" + normalized.split("/").filter(Boolean).join("/");
        }

        function splitRoutePath(path) {
          const normalized = normalizeRoutePath(path);
          if (normalized === "/") return [];
          return normalized.slice(1).split("/");
        }

        function matchGateRoute(routes, routePath) {
          if (!Array.isArray(routes)) return null;

          const currentSegments = splitRoutePath(routePath);

          for (let index = 0; index < routes.length; index += 1) {
            const route = routes[index];
            if (!route || !route.path) continue;

            const routeSegments = splitRoutePath(route.path);
            if (routeSegments.length !== currentSegments.length) continue;

            const params = {};
            let matches = true;

            for (let segmentIndex = 0; segmentIndex < routeSegments.length; segmentIndex += 1) {
              const expected = routeSegments[segmentIndex];
              const actual = currentSegments[segmentIndex];

              if (expected.charAt(0) === ":") {
                const paramName = expected.slice(1);
                if (paramName) params[paramName] = decodeURIComponent(actual || "");
                continue;
              }

              if (expected !== actual) {
                matches = false;
                break;
              }
            }

            if (matches) return { route, params };
          }

          return null;
        }

        function resolveSessionUser(session) {
          if (!session || typeof session !== "object") return null;
          if (session.user && typeof session.user === "object") return session.user;
          if (session.data && session.data.user && typeof session.data.user === "object") return session.data.user;
          if (session.data && typeof session.data === "object" && session.data.id) return session.data;
          return session.id ? session : null;
        }

        function hasAuthenticatedUser(session) {
          const user = resolveSessionUser(session);
          return Boolean(user && user.id);
        }

        const GateRouterView = {
          name: "GateRouterView",
          setup() {
            const authState = window.Vue.reactive({
              checking: false,
              allowedPath: "",
              redirectingPath: ""
            });
            let authCheckId = 0;
            const matchedRoute = window.Vue.computed(function () {
              return matchGateRoute(page.routes || [], routeState.routePath || "/");
            });

            window.Vue.watch(matchedRoute, function (match) {
              routeState.params = match && match.params ? match.params : {};
              authCheckId += 1;
              const currentCheckId = authCheckId;

              if (!match || !match.route || match.route.requiresAuth !== true) {
                authState.checking = false;
                authState.allowedPath = "";
                authState.redirectingPath = "";
                return;
              }

              const pathForCheck = routeState.routePath || "/";
              authState.checking = true;
              authState.allowedPath = "";

              Gate.api.auth.getSession()
                .then(function (session) {
                  if (currentCheckId !== authCheckId) return;

                  if (hasAuthenticatedUser(session)) {
                    authState.allowedPath = pathForCheck;
                    authState.checking = false;
                    return;
                  }

                  authState.redirectingPath = pathForCheck;
                  Gate.api.auth.loginRedirect((routeState.pathname || "/") + (routeState.search || "") + (routeState.hash || ""));
                })
                .catch(function () {
                  if (currentCheckId !== authCheckId) return;
                  authState.redirectingPath = pathForCheck;
                  Gate.api.auth.loginRedirect((routeState.pathname || "/") + (routeState.search || "") + (routeState.hash || ""));
                });
            }, { immediate: true });

            return function () {
              const match = matchedRoute.value;
              if (!match || !match.route) {
                return window.Vue.h("div", "Route not found");
              }

              if (match.route.requiresAuth === true) {
                const activePath = routeState.routePath || "/";
                if (authState.allowedPath !== activePath) {
                  return window.Vue.h("div", "Redirecionando...");
                }
              }

              const componentName = match.route.pageCode || match.route.widgetCode;
              if (!componentName) {
                return window.Vue.h("div", "Route not found");
              }

              const staticProps = match.route.props && typeof match.route.props === "object"
                ? match.route.props
                : {};
              const resolvedProps = Object.assign({}, staticProps, match.params || {});

              return window.Vue.h(window.Vue.resolveComponent(componentName), resolvedProps);
            };
          }
        };


        try {
          const component = compileDefinition(page.script, fallbackTemplate, page);
          const app = window.Vue.createApp(component);
          app.config.globalProperties.$gate = Gate;
          app.config.globalProperties.$page = page;
          app.config.globalProperties.$i18n = Gate.i18n;
          app.config.globalProperties.$t = Gate.i18n.t.bind(Gate.i18n);
          if (window.__prmEnsureFormRuntime) {
            await window.__prmEnsureFormRuntime();
            if (window.__prmRegisterFormComponents) {
              window.__prmRegisterFormComponents(app);
            }
          }
          registerPageComponents(app, page);
          registerWidgets(app, page);
          app.component("gate-router-view", GateRouterView);
          const mountStart = performance.now();
          app.mount(root);
          const timing = window.__prmGateTiming || {};
          window.parent.postMessage({
            source,
            type: "gate:ready",
            payload: {
              timing: {
                vueScriptMs: typeof timing.vueScriptEnd === "number" && typeof timing.vueScriptStart === "number"
                  ? Math.round(timing.vueScriptEnd - timing.vueScriptStart)
                  : null,
                mountMs: Math.round(performance.now() - mountStart)
              }
            }
          }, "*");
        } catch (error) {
          console.error("Gate runtime bootstrap failed", error);
          reportRuntimeError("bootstrap", error);
          root.innerHTML = '<pre role="alert" style="padding:16px;white-space:pre-wrap;color:#b91c1c;">' + String(error) + '</pre>';
        }
      })();
    </script>
  </body>
</html>`;
};
